/**
 * Shared Core — Platform-Agnostic Type Definitions
 *
 * All flight planning, weather, export/import, and navigation types
 * shared between the web and mobile apps. No native dependencies.
 */

// ─── Aircraft Category (inlined from logbook-types to avoid native dep) ──
export type AircraftCategory = 'Airplane' | 'Rotorcraft' | 'Other';

// ─── Flight Category ─────────────────────────────────────────────────
export type FlightCategory = 'VFR' | 'MVFR' | 'IFR' | 'LIFR';

export interface ParsedMetar {
  stationId: string;
  observationTime: number;
  latitude: number;
  longitude: number;
  windDirection: number;
  windSpeed: number;
  windGust: number | null;
  visibility: number;
  ceiling: number | null;
  temperature: number;
  dewpoint: number;
  altimeter: number;
  weatherPhenomena: string[];
  flightCategory: FlightCategory;
  rawText: string;
}

export interface TafForecastGroup {
  changeIndicator: 'FM' | 'TEMPO' | 'BECMG' | 'PROB' | 'INITIAL';
  probability: number | null;
  fromTime: number;
  toTime: number;
  windDirection: number;
  windSpeed: number;
  windGust: number | null;
  visibility: number;
  ceiling: number | null;
  weatherPhenomena: string[];
}

export interface ParsedTaf {
  stationId: string;
  issueTime: number;
  validFrom: number;
  validTo: number;
  latitude: number;
  longitude: number;
  forecastGroups: TafForecastGroup[];
  rawText: string;
}

// ─── Geographic Primitives ──────────────────────────────────────────────
export interface LatLng {
  latitude: number;
  longitude: number;
}

// ─── Waypoint Types ─────────────────────────────────────────────────────
export type WaypointType = 'airport' | 'navaid' | 'fix' | 'user_defined';

export interface ResolvedWaypoint {
  identifier: string;
  name: string;
  type: WaypointType;
  latitude: number;
  longitude: number;
  elevation?: number;
  longestRunwayFt?: number;
  magneticVariation?: number;
  source: 'nasr' | 'navaid' | 'designated_point' | 'user';
}

export interface RouteWaypoint extends ResolvedWaypoint {
  sequenceIndex: number;
  plannedAltitude: number;
  altitudeConstraint?: 'at' | 'at_or_above' | 'at_or_below';
}

// ─── Route Types ────────────────────────────────────────────────────────
export interface WindData {
  direction: number;
  speed: number;
}

export interface LegMetrics {
  distanceNm: number;
  trueCourse: number;
  magneticCourse: number;
  estimatedTimeMinutes: number;
  groundSpeed?: number;
}

export interface RouteLeg {
  from: RouteWaypoint;
  to: RouteWaypoint;
  distanceNm: number;
  trueCourse: number;
  magneticCourse: number;
  estimatedTimeMinutes: number;
  plannedAltitude: number;
  airwayId?: string;
}

export interface Route {
  waypoints: RouteWaypoint[];
  legs: RouteLeg[];
  totalDistanceNm: number;
  totalTimeMinutes: number;
}

export interface RouteParams {
  departure: ResolvedWaypoint;
  destination: ResolvedWaypoint;
  intermediates: ResolvedWaypoint[];
  flightRules: 'VFR' | 'IFR';
  cruiseAltitude: number;
  trueAirspeed: number;
  departureTime?: number;
}

// ─── Navigation Log Types ───────────────────────────────────────────────
export interface NavigationLog {
  legs: NavLogLeg[];
  totalDistanceNm: number;
  totalTimeMinutes: number;
  totalFuelGallons: number;
  aircraftId: string;
  generatedAt: number;
}

export interface NavLogLeg {
  fromWaypoint: string;
  toWaypoint: string;
  trueCourse: number;
  magneticCourse: number;
  windCorrectedHeading: number;
  groundSpeed: number;
  distanceNm: number;
  estimatedTimeMinutes: number;
  fuelBurnGallons: number;
  cumulativeDistanceNm: number;
  cumulativeFuelGallons: number;
  plannedAltitude: number;
  frequencies: LegFrequency[];
}

export interface LegFrequency {
  name: string;
  frequency: string;
  type: 'tower' | 'ground' | 'approach' | 'center' | 'ctaf' | 'atis' | 'unicom';
}

// ─── Aircraft Performance ───────────────────────────────────────────────
export interface AircraftPerformance {
  profileId: string;
  tailNumber: string;
  typeDesignator: string;
  cruiseTas: number;
  fuelBurnGph: number;
  usableFuelGallons: number;
  fuelType: string;
  bestGlideSpeed?: number;
  glideRatio?: number;
  maxCrosswindKts?: number;
  category: AircraftCategory;
}

export const DEFAULT_PERFORMANCE: AircraftPerformance = {
  profileId: 'default',
  tailNumber: 'N/A',
  typeDesignator: 'ZZZZ',
  cruiseTas: 120,
  fuelBurnGph: 10,
  usableFuelGallons: 50,
  fuelType: '100LL',
  category: 'Airplane',
};

// ─── Flight Plan Core ───────────────────────────────────────────────────
export type FlightPlanStatus = 'draft' | 'filed' | 'active' | 'completed' | 'cancelled';
export type FilingStatus = 'not_filed' | 'pending' | 'filed' | 'activated' | 'cancelled' | 'queued';

export type HazardType =
  | 'convective' | 'turbulence' | 'icing'
  | 'ifr' | 'mountain_obscuration' | 'volcanic_ash'
  | 'sandstorm' | 'other';

export type AirspaceType =
  | 'P' | 'R' | 'MOA' | 'A' | 'W'
  | 'ClassB' | 'ClassC' | 'ClassD' | 'ClassE'
  | 'TFR';

export interface AirspaceConflict {
  airspaceName: string;
  airspaceType: AirspaceType;
  altitudeFloor: number;
  altitudeCeiling: number;
  schedule: string | null;
  isActive: boolean;
  entryDistanceNm: number;
  exitDistanceNm: number;
  segmentIndices: number[];
  severity: 'prohibited' | 'restricted' | 'advisory';
}

export interface ClearanceResult {
  legs: LegClearance[];
  overallSafe: boolean;
  warnings: ClearanceWarning[];
}

export interface LegClearance {
  legIndex: number;
  msaFt: number;
  plannedAltFt: number;
  isSafe: boolean;
  highestTerrainFt: number;
  highestObstacleFt: number;
  isMountainous: boolean;
}

export type ClearanceWarningType = 'below_msa' | 'obstacle_threat' | 'below_mea';

export interface ClearanceWarning {
  type: ClearanceWarningType;
  legIndex: number;
  description: string;
  msaFt: number;
  plannedAltFt: number;
  obstacleId?: string;
}

export interface RouteWeatherAssessment {
  departureWeather: AirportWeather;
  destinationWeather: AirportWeather;
  enrouteHazards: EnrouteHazard[];
  routeSummary: RouteSummaryCategory;
  windsAloft: WindsAloftLeg[];
  dataAge: number;
  isStale: boolean;
}

export interface AirportWeather {
  stationId: string;
  metar: ParsedMetar | null;
  taf: ParsedTaf | null;
  flightCategory: FlightCategory;
}

export interface EnrouteHazard {
  type: HazardType;
  advisoryType: 'SIGMET' | 'AIRMET' | 'G-AIRMET' | 'PIREP';
  altitudeLow: number;
  altitudeHigh: number;
  validFrom: number;
  validTo: number;
  affectedLegIndices: number[];
  description: string;
  severity: 'light' | 'moderate' | 'severe' | 'extreme';
}

export type RouteSummaryCategory = 'VFR' | 'MVFR' | 'IFR' | 'LIFR';

export interface WindsAloftLeg {
  legIndex: number;
  windDirection: number;
  windSpeed: number;
  temperature: number;
  altitude: number;
}

export interface FlightPlan {
  id: string;
  name: string;
  status: FlightPlanStatus;
  flightRules: 'VFR' | 'IFR';
  departure: ResolvedWaypoint;
  destination: ResolvedWaypoint;
  waypoints: RouteWaypoint[];
  legs: RouteLeg[];
  cruiseAltitude: number;
  trueAirspeed: number;
  aircraftProfileId: string | null;
  totalDistanceNm: number;
  totalTimeMinutes: number;
  totalFuelGallons: number | null;
  filingReference: string | null;
  filingStatus: FilingStatus;
  createdAt: number;
  updatedAt: number;
  departureTime: number | null;
  navLog: NavigationLog | null;
  weatherAssessment: RouteWeatherAssessment | null;
  conflicts: AirspaceConflict[];
  clearanceResult: ClearanceResult | null;
}

export interface FlightPlanParams {
  name?: string;
  departure: string;
  destination: string;
  intermediates?: string[];
  flightRules: 'VFR' | 'IFR';
  cruiseAltitude: number;
  trueAirspeed?: number;
  aircraftProfileId?: string;
  departureTime?: number;
}

export interface FlightPlanSummary {
  id: string;
  name: string;
  departure: string;
  destination: string;
  flightRules: 'VFR' | 'IFR';
  status: FlightPlanStatus;
  filingStatus: FilingStatus;
  totalDistanceNm: number;
  createdAt: number;
  updatedAt: number;
}

// ─── Export/Import Types ────────────────────────────────────────────────
export type ExportFormat = 'icao-fpl' | 'garmin-fpl' | 'gpx' | 'kml' | 'geojson';

// ─── Fuel Stop Planning ─────────────────────────────────────────────────
export interface FuelStopCandidate {
  airport: ResolvedWaypoint;
  deviationNm: number;
  distanceAlongRouteNm: number;
  fuelTypes: string[];
  longestRunwayFt: number;
  insertionIndex: number;
}

// ─── Wind Component Analysis ────────────────────────────────────────────
export interface RunwayWindAnalysis {
  runwayId: string;
  runwayHeading: number;
  crosswindKts: number;
  headwindKts: number;
  exceedsCrosswindLimit: boolean;
}
